import AddBoxIcon from '@mui/icons-material/AddBox';
import { Box, Grid, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import TreatmentApi from 'api/dentists/treatment.api';
import TreatmentHistoryApi from 'api/dentists/treatment-history.api';
import ClinicApi from 'api/master/clinic.api';
import { swalDelete, swalWarning } from 'component/Alert/Swal';
import ButtonCustom from 'component/Button/ButtonCustom';
import Loading from 'component/Loading';
import PopupCreateByUsers from 'component/Treatments/PopupCreateByUsers';
import PopupTreatmentPlanForm from 'component/Treatments/PopupTreatmentPlanForm';
import TxInformationBox from 'component/Treatments/TxInformationBox';
import { notiError, notiSuccess } from 'component/notifications/notifications';
import { IconAddBox, IconAddBoxDim } from 'features/treatments/MainMenu/OPD/style/UseStyle';
import { icons } from 'constants/images';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getBranch, numberFormat } from 'utils/app.utils';
import { dateTimeToView, dateToView } from 'utils/date.utils';
import { colors } from 'constants/theme';
import { Config } from 'config';

/** UTILS || SLICE */
import OperativeTypeApi from 'api/setting/treatments/operative-type.api';
import OperativeApi from 'api/setting/treatments/operative.api';
import {
  historyToothChart,
  setColorToothChart,
  setToothChartSelected,
  setTreatmentByKey,
  toothChartSelected,
  treatment,
  TreatmentStateInterface
} from 'app/slice/treatment.slice';
import { selectMe } from 'app/slice/user.slice';
import {
  deriveTreatmentDisplayStatus,
  getTxpAdditionalNote,
  getTxpDefaultDoctorId,
  getTxpDoctorLabel,
  getTreatmentStatusTranslationKey,
  isTreatmentPlanItemAddDisabled,
  isTreatmentPlanItemEditDisabled,
  isTreatmentPlanItemInSummary,
  sortTreatmentPlansByDisplayStatus,
} from 'component/Treatments/treatment-plan-status.utils';
import { useLocation } from 'react-router-dom';
import useWindowSize from 'utils/useWindowSize';
import {
  getTreatmentPlanOperationalPrice,
  getTreatmentPlanPriceDisplayForTheme,
} from 'component/Treatments/treatment-plan-price-range.utils';
import { normalizeTreatmentPatientId } from 'component/Treatments/treatment-patient-id.utils';
import { getTreatmentPlanPermissions } from 'component/Treatments/treatment-plan-permissions.utils';
import { getTdhTxpTableLayout, isTdhTxpWideLayout } from 'component/Treatments/treatment-plan-ui.utils';
import {
  buildTreatmentPlanPrintHistoryPayload,
  createTreatmentPlanPrintHistoryDeduper,
  TreatmentPlanPrintDocumentKey,
  TreatmentPlanPrintSignatureState,
} from 'component/Treatments/treatment-plan-print-history';

export const Item = styled(Box)(({ theme }) => ({
  margin: '0 -0.5rem -1rem',
  maxHeight: 400,
  overflowY: 'auto'
}))

export const TableWidth = styled(Box)(({ theme }) => ({
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  '.MuiTable-root': {
    tableLayout: 'fixed',
    minWidth: 'unset',
    maxWidth: '100%',
    '.MuiTableHead-root': {
      '.MuiTableCell-root': {
        paddingLeft: 8,
        paddingRight: 8
      }
    },
    '.MuiTableBody-root': {
      '.MuiTableCell-body': {
        paddingLeft: 8,
        paddingRight: 8
      }
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: 1200,
    },
  },
  '&.txp-table .MuiTable-root': {
    minWidth: 1200,
    maxWidth: 'none',
  },
  '&.txp-table .MuiTableContainer-root': {
    overflowX: 'auto',
  },
  '&.txp-table': {
    overflow: 'visible',
  },
}))

type TxpType = {
  patient?: any
  history?: boolean
  hasCreateTreatmentPlan?: 'PERMISS' | 'UNPERMISS'
  hasDeleteTreatmentPlan?: 'PERMISS' | 'UNPERMISS'
  type?: any
}

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export default function TXP(props: TxpType) {
  const user = useSelector(selectMe)
  if (!getTreatmentPlanPermissions({ user }).canView) return null
  return <TreatmentPlans {...props} />
}

function TreatmentPlans(props: TxpType) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const location = useLocation()
  const treatments: TreatmentStateInterface = useSelector(treatment)
  const valueToothChartSelected = useSelector(toothChartSelected)
  const historyToothCharts = useSelector(historyToothChart)
  const user = useSelector(selectMe)
  const { canCreate, canEdit, canDelete } = getTreatmentPlanPermissions({
    user,
    createPermission: props.hasCreateTreatmentPlan,
    deletePermission: props.hasDeleteTreatmentPlan,
    readOnly: props.history || props.type === 'RetrospectiveEdit',
  })
  const isTdh = Config.IS_TDH === '1'
  const txpDateLabel = t(isTdh ? 'TXP.TABLE.DATE_APPOINTMENT' : 'TXP.TABLE.DATE')
  const txpDoctorLabel = t(isTdh ? 'TXP.TABLE.DENTIST' : 'TXP.TABLE.DOCTOR')
  const summeryTreatmentsRef = useRef(treatments.summeryTreatments)
  const [txp, setTxp] = useState<any>()
  const orderedTxp = useMemo(() => {
    if (isTdh && !props.history && Array.isArray(txp)) {
      return sortTreatmentPlansByDisplayStatus(
        txp,
        _.map(treatments.summeryTreatments, 'refId'),
      )
    }
    return txp
  }, [isTdh, props.history, treatments.summeryTreatments, txp])
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [operativeTypes, setOperativeTypes] = useState<any>([])
  const [header, setHeader] = useState<any>('')
  const [edit, setEdit] = useState<any>('')
  const [width] = useWindowSize()
  const isWideTxpLayout = isTdhTxpWideLayout(isTdh, width)
  const tdhTxpTableLayout = getTdhTxpTableLayout(isTdh, width)
  const [createByUsers, setCreateByUsers] = useState(false)
  const [createTemplate, setCreateTemplate] = useState(false)
  const [loading, setLoading] = useState(true);
  const [addingItemIds, setAddingItemIds] = useState<Set<string>>(new Set())
  const addingItemIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    summeryTreatmentsRef.current = treatments.summeryTreatments
  }, [treatments.summeryTreatments])

  const [showPlanForm, setShowPlanForm] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<any>(null)

  const [opertatives, setOperative] = useState<any[]>([])
  const [operativeType, setOperativeType] = useState<any[]>([])
  const patientId = normalizeTreatmentPatientId(props.patient?.patientId)
  const printHistoryDeduperRef = useRef(createTreatmentPlanPrintHistoryDeduper())

  const onTreatmentPlanPrinted = (
    plan: any,
    documentKey: TreatmentPlanPrintDocumentKey,
    signatureState?: TreatmentPlanPrintSignatureState,
  ) => {
    const branchId = Number(getBranch())
    const patientCaseId = Number(plan?.patientCaseId || props.patient?.patientCase?.patientCaseId || props.patient?.patientCaseId || 0)
    if (!plan || !patientId || !branchId || !patientCaseId) return
    if (!printHistoryDeduperRef.current(documentKey, plan)) return

    const payload = buildTreatmentPlanPrintHistoryPayload({
      documentKey,
      branchId,
      plan,
      patient: props.patient,
      doctors,
      signatureState,
    })
    void TreatmentHistoryApi.createHistoryDocument(payload).catch(() => undefined)
  }

  const loadData = useCallback(() => {
    if (!patientId) {
      setTxp([])
      setLoading(false)
      return
    }

    TreatmentApi.getTxp(patientId).then((res) => {
      if (res.status === 200) {
        setTxp(res.data)
        if (selectedPlan) {
          const updated = res.data?.treatmentPlans?.find((p: any) => p.patientTreatmentPlanId === selectedPlan.patientTreatmentPlanId)
          if (updated) setSelectedPlan(updated)
        }
      }
    }).finally(() => setLoading(false))
  }, [patientId, selectedPlan?.patientTreatmentPlanId])

  const loadDoctor = async () => {
    const resp = await ClinicApi.findAllDoctors().finally(() => setLoading(false))
    if (resp.status === 200) setDoctors(resp.data)
  }

  const loadDataOperativeType = useCallback(() => {
    OperativeApi.findAll({ export: 1 }).then(({ data }) => {
      setOperative(() => {
        const newData: any = data.map((item: any) => {
          const mapData = {
            itemCode: item?.operativeCode,
            itemId: item?.operativeId,
            itemName: item?.operativeName,
            itemNameEn: item?.operativeNameEn,
            itemTypeId: item?.operativeTypeId,
            itemTypeName: item?.operative_type?.operativeTypeName,
            itemTypeNameEn: item?.operative_type?.operativeTypeNameEn,
            itemUnitName: item?.operative_unit?.operativeUnitName,
            itemUnitNameEn: item?.operative_unit?.operativeUnitNameEn,
            price: item.salePrice,
          }
          return mapData
        })
        return newData
      })
    }).catch((e) => { return })

    OperativeTypeApi.findAll({ export: 1 }).then(({ data }) => {
      setOperativeType(() => {
        const newData: any = data.map((item: any) => {
          const mapData = {
            itemTypeId: item.operativeTypeId,
            itemTypeName: item.operativeTypeName,
            itemTypeNameEn: item.operativeTypeNameEn
          }
          return mapData
        })
        return newData
      })
    }).catch((e) => { return })

    TreatmentApi.getTxpTypes().then((res) => {
      if (res.status === 200) setOperativeTypes(res.data)
    }).finally(() => setLoading(false))

  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    loadDoctor()
  }, [])

  useEffect(() => {
    loadDataOperativeType()
  }, [])

  const popupCreateTemplate = () => {
    if (!canCreate) return
    if (!patientId) return notiError(t('APPOINTMENT.MESSAGE.SELECT_PATIENT'))
    setCreateByUsers(true)
    setCreateTemplate(true)
    setHeader(t('TXP.POPUP.HEADER_TEMPLATE'))
  }

  const popupCreateByUsers = () => {
    if (!canCreate) return
    if (!patientId) return notiError(t('APPOINTMENT.MESSAGE.SELECT_PATIENT'))
    setCreateByUsers(true)
    setCreateTemplate(false)
    setHeader(t('TXP.POPUP.HEADER_BY_USER'))
  }

  const onEdit = (val: any) => {
    if (!canEdit) return
    setEdit(val)
    setHeader(t('TXP.POPUP.HEADER_EDIT'))
    setCreateByUsers(true)
    setCreateTemplate(false)
  }

  const updateStatus = (data: any, type: string) => {
    const isDeletion = type === 'DELETED' || type === 'CANCEL'
    if (isDeletion ? !canDelete : !canEdit) return
    const { patientTreatmentPlanId, treatmentPlanName, createdAt } = data
    const status = { status: type }
    if (type === 'PENDING') {
      swalWarning(t('TXP.MESSAGE.TITLE_OPEN_TXP'), `${t('TXP.MESSAGE.OPEN_DO_TXP')} ${treatmentPlanName} ${dateTimeToView(createdAt)} ${t('TXP.MESSAGE.YES_OR_NO')}`, (res: any) => {
        TreatmentApi.upDatePatientTreatmentPlans(patientTreatmentPlanId, status)
          .then((resp) => {
            notiSuccess(t('TXP.MESSAGE.OPEN_TXP_SUCCESS'), '', null, null, !props.history ? 'register-page' : '')
            loadData()
          })
          .catch((e) => {
            notiError(t('TXP.MESSAGE.OPEN_TXP_FAIL'), '', null, null, !props.history ? 'register-page' : '')
          })
      }, {
        showCancelButton: true,
        allowOutsideClick: true,
        confirmButtonText: t('TXP.MESSAGE.OPEN_TXP'),
        cancelButtonText: t('BUTTON.CANCEL')
      })
    } else {
      swalDelete(
        (type === 'DELETED' && t('TXP.MESSAGE.DEL_TXP_YES_OR_NO')) || (type === 'CLOSE' && t('TXP.MESSAGE.CLOSE_TXP_YES_OR_NO')) || t('TXP.MESSAGE.CANCEL_TXP_YES_OR_NO'),
        `${(type === 'DELETED' && t('TXP.MESSAGE.TODO_DEL_TXP')) || (type === 'PENDING' && t('TXP.MESSAGE.TODO_OPEN_TXP')) || (type === 'CLOSE' && t('TXP.MESSAGE.TODO_CLOSE_TXP')) || t('TXP.MESSAGE.TODO_CANCEL_TXP')}
        ${treatmentPlanName} ${dateTimeToView(createdAt)} ${t('TXP.MESSAGE.YES_OR_NO')}`,
        (res: any) => {
          if (res) {
            TreatmentApi.upDatePatientTreatmentPlans(patientTreatmentPlanId, status)
              .then((resp) => {
                notiSuccess(t(type === 'DELETED' ? 'TXP.MESSAGE.DEL_SUCCESS' : type === 'CLOSE' ? 'TXP.MESSAGE.CLOSE_SUCCESS' : 'TXP.MESSAGE.CANCEL_SUCCESS'), '', null, null, !props.history ? 'register-page' : '')
                loadData()
              })
              .catch((e) => {
                notiError(t(type === 'DELETED' ? 'TXP.MESSAGE.DEL_FAIL' : type === 'CLOSE' ? 'TXP.MESSAGE.CLOSE_FAIL' : 'TXP.MESSAGE.CANCEL_FAIL'), '', null, null, !props.history ? 'register-page' : '')
              })
          }
        },
        {
          confirmButtonText: t(type === 'DELETED' ? 'TXP.MESSAGE.DEL_TXP' : type === 'CLOSE' ? 'TXP.MESSAGE.CLOSE_TXP' : type === 'PENDING' ? 'TXP.MESSAGE.OPEN_TXP' : 'TXP.MESSAGE.CANCEL_TXP'),
          cancelButtonText: (t('BUTTON.CANCEL'))
        }
      )
    }
  }

  const getAddItemKey = (tx: any): string => String(tx.id ?? `${tx.operativeId}:${tx.teeth || ''}`)

  const handleAddOperative = async (tx: any, planRemark?: string) => {
    if (!isTdh) {
      const item = {
        operativeCode: tx.operativeCode,
        operativeId: tx.operativeId,
        operativeName: tx.operativeName,
        operativeNameEn: tx.operativeNameEn,
        operativeTypeId: tx.operativeTypeId,
        operativeTypeName: tx.operativeTypeName,
        operativeTypeNameEn: tx.operativeTypeNameEn,
        operativeUnitName: tx.operativeUnitName,
        operativeUnitNameEn: tx.operativeUnitNameEn,
        tempOrderTreatmentId: null,
        teeth: tx.teeth || '',
        teethJson: tx.teethJson || '',
        teethType: tx.teethType || '',
        qty: 1,
        price: tx.price,
        doctorId: tx.doctorId || user.userId || 0,
        discount: 0,
        additional: 0,
        discountUnit: 'BAHT',
        additionalUnit: 'BAHT',
        total: tx.price,
        dfAmount: null,
        cfAmount: null,
        dfDiscountAmount: 0,
        cfDiscountAmount: 0,
        dfDiscountUnit: 'BAHT',
        cfDiscountUnit: 'BAHT',
        note: tx.remark || '',
        noteNextVisit: '',
        refType: 'PLAN',
        refId: tx.id,
      }
      if (tx.teethJson) {
        const toothChart = JSON.parse(tx.teethJson)
        const newArray: any = [];
        _.map(treatments.summeryTreatments, (summery: any) => {
          if (summery.teethJson) newArray.push(JSON.parse(summery.teethJson))
        })
        newArray.push(toothChart)
        dispatch(setToothChartSelected([...historyToothCharts, ...newArray]))
      }

      dispatch(setTreatmentByKey({ key: 'summeryTreatments', value: [...treatments.summeryTreatments, item] }))
      dispatch(setColorToothChart({ value: tx.color }))
      return
    }

    const addItemKey = getAddItemKey(tx)
    if (
      addingItemIdsRef.current.has(addItemKey) ||
      isTreatmentPlanItemInSummary(summeryTreatmentsRef.current, tx.id)
    ) return

    addingItemIdsRef.current.add(addItemKey)
    setAddingItemIds((current) => new Set(current).add(addItemKey))

    try {
      const price = getTreatmentPlanOperationalPrice(tx)

      const item = {
        operativeCode: tx.operativeCode,
        operativeId: tx.operativeId,
        operativeName: tx.operativeName,
        operativeNameEn: tx.operativeNameEn,
        operativeTypeId: tx.operativeTypeId,
        operativeTypeName: tx.operativeTypeName,
        operativeTypeNameEn: tx.operativeTypeNameEn,
        operativeUnitName: tx.operativeUnitName,
        operativeUnitNameEn: tx.operativeUnitNameEn,
        tempOrderTreatmentId: null,
        teeth: tx.teeth || '',
        teethJson: tx.teethJson || '',
        teethType: tx.teethType || '',
        qty: 1,
        price,
        doctorId: tx.doctorId || getTxpDefaultDoctorId(user?.userId, doctors),
        discount: 0,
        additional: 0,
        discountUnit: 'BAHT',
        additionalUnit: 'BAHT',
        total: price,
        dfAmount: null,
        cfAmount: null,
        dfDiscountAmount: 0,
        cfDiscountAmount: 0,
        dfDiscountUnit: 'BAHT',
        cfDiscountUnit: 'BAHT',
        note: getTxpAdditionalNote(tx.remark, planRemark),
        noteNextVisit: '',
        refType: 'PLAN',
        refId: tx.id,
      }
      if (tx.teethJson) {
        const toothChart = JSON.parse(tx.teethJson)
        const newArray: any = [];
        _.map(summeryTreatmentsRef.current, (summery: any) => {
          if (summery.teethJson) newArray.push(JSON.parse(summery.teethJson))
        })
        newArray.push(toothChart)
        dispatch(setToothChartSelected([...historyToothCharts, ...newArray]))
      }

      const updatedSummeryTreatments = [...summeryTreatmentsRef.current, item]
      summeryTreatmentsRef.current = updatedSummeryTreatments
      dispatch(setTreatmentByKey({ key: 'summeryTreatments', value: updatedSummeryTreatments }))
      dispatch(setColorToothChart({ value: tx.color }))
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      notiError(t(message === 'INVALID_TREATMENT_PLAN_PRICE_RANGE'
        ? 'TXP.MESSAGE.PRICE_RANGE_INVALID'
        : 'TXP.MESSAGE.PRICE_MASTER_LOAD_FAILED'))
    } finally {
      addingItemIdsRef.current.delete(addItemKey)
      setAddingItemIds((current) => {
        const next = new Set(current)
        next.delete(addItemKey)
        return next
      })
    }
  }

  const defaultHeadCells = [
    { id: 'no', disablePadding: false, align: 'center', label: '#', width: isWideTxpLayout ? '5%' : '50px' },
  ]

  const teethHeadCell = {
    id: 'teeth',
    disablePadding: false,
    align: 'left',
    label: t('TXP.TABLE.TOOTH'),
    width: '10%',
  }

  const ItemsHeadCells = [
    { id: 'message', disablePadding: false, align: 'left', label: t('TXP.TABLE.TYPE'), width: '12%' },
    { id: 'message', disablePadding: false, align: 'center', label: t('TXP.TABLE.CODE'), width: tdhTxpTableLayout?.code },
    { id: 'message', disablePadding: false, align: 'left', label: t('TXP.TABLE.DETAIL'), width: '20%' },
    { id: 'message', disablePadding: false, align: 'right', label: t('TXP.TABLE.PRICE'), width: tdhTxpTableLayout?.price },
    ...(!isTdh ? [
      { id: 'message', disablePadding: false, align: 'left', label: <span style={{ whiteSpace: 'nowrap' }}>{txpDateLabel}</span>, width: tdhTxpTableLayout?.date || '160px' },
      { id: 'message', disablePadding: false, align: 'left', label: <span style={{ whiteSpace: 'nowrap' }}>{txpDoctorLabel}</span>, width: tdhTxpTableLayout?.doctor || '15%' },
      { id: 'message', disablePadding: false, align: 'left', label: t('TXP.TABLE.TIME'), width: tdhTxpTableLayout?.duration },
    ] : []),
    { id: 'message', disablePadding: false, align: 'center', label: <span style={{ whiteSpace: 'nowrap' }}>{t('TXP.TABLE.STATUS')}</span>, width: tdhTxpTableLayout?.status || '120px' },
  ]

  const headCells = isWideTxpLayout ? [
    ...defaultHeadCells,
    ...(isTdh ? [teethHeadCell] : []),
    ...ItemsHeadCells,
    { id: 'message', disablePadding: false, align: 'center', label: t('Action'), width: tdhTxpTableLayout?.action || '7%' }] : [
    ...defaultHeadCells,
    { id: 'message', disablePadding: false, align: 'left', label: t('') }, {
      id: 'message',
      disablePadding: false, align: 'center', label: t('Action'), width: '90px'
    }]

  const headCellsHistory = isWideTxpLayout ? [
    ...defaultHeadCells,
    ...(isTdh ? [teethHeadCell] : []),
    ...ItemsHeadCells] : [
    ...defaultHeadCells,
    { id: 'message', disablePadding: false, align: 'left', label: t('') }]

  const renderItemStatus = (data: any) => {
    if (!isTdh) {
      return _.some(treatments.summeryTreatments, { refId: data.id }) && t('TX_INFORMATION.STATUS.IN_PROGRESS') ||
        data.status === 'DONE' && t('TX_INFORMATION.STATUS.DONE') ||
        t('TX_INFORMATION.STATUS.PENDING')
    }

    return t(getTreatmentStatusTranslationKey(
      deriveTreatmentDisplayStatus({
        planStatus: data.status,
        items: [data],
        activeTreatmentIds: _.map(treatments.summeryTreatments, 'refId'),
      }),
      true,
    ))
  }

  const renderPlanPrice = (data: any) => {
    if (!isTdh) return numberFormat(data.price) || '-'

    const priceDisplay = getTreatmentPlanPriceDisplayForTheme(data, true)
    const primaryPrice = numberFormat(priceDisplay.primary) || '-'
    if (!priceDisplay.range) return primaryPrice
    return (
      <Box sx={{ lineHeight: 1.25 }}>
        <Typography component="div">{primaryPrice}</Typography>
        <Typography component="div" sx={{ color: colors.disabledGray, fontSize: 12 }}>
          ({numberFormat(priceDisplay.range.min)} - {numberFormat(priceDisplay.range.max)})
        </Typography>
      </Box>
    )
  }

  const renderData = (
    data: any,
    no: number,
    status: any,
    _planIndex?: number,
    planCreator?: unknown,
    planRemark?: string,
    onEditItem?: () => void,
    isPlanEditDisabled = false,
  ) => {
    // Creating a new plan is permission-gated by the buttons above. It must
    // not disable adding an unfinished item from an existing TDH plan.
    const hasAdd = !isTdh && user?.hasCreateTreatmentPlan !== 'PERMISS' && (location?.pathname === "/payment/create" || location?.pathname === "/retrospective-order")
    const doctorLabel = isTdh
      ? getTxpDoctorLabel({
        doctorId: data.doctorId,
        doctors,
        creatorName: planCreator,
        creatorLabel: t('TXP.TABLE.PLAN_CREATOR'),
      })
      : _.find(doctors, { userId: data.doctorId })?.fullname || '-'
    const itemRemark = isTdh ? getTxpAdditionalNote(data.remark) : ''
    const addItemKey = getAddItemKey(data)
    const isAdding = isTdh && addingItemIds.has(addItemKey)
    const isAddDisabled = isTreatmentPlanItemAddDisabled({
      planStatus: status,
      itemStatus: data.status,
      isAdding,
      isAlreadyInSummary: isTreatmentPlanItemInSummary(treatments.summeryTreatments, data.id),
    }) || hasAdd
    const isItemEditDisabled = isPlanEditDisabled || isTreatmentPlanItemEditDisabled({
      planStatus: status,
      itemStatus: data.status,
      isAlreadyInSummary: isTreatmentPlanItemInSummary(treatments.summeryTreatments, data.id),
    })
    const renderEditButton = () => {
      if (!onEditItem) return null
      if (!canEdit) return null

      const button = (
        <IconButton
          size="small"
          className="p-0"
          disabled={isItemEditDisabled}
          onClick={onEditItem}
          aria-label={t('TX_INFORMATION.BUTTON.EDIT')}
          sx={{ width: 30, height: 30 }}
        >
          <img
            src={icons.dropdownEdit}
            alt=""
            style={{ width: 18, height: 18, opacity: isItemEditDisabled ? .3 : 1 }}
          />
        </IconButton>
      )

      return (
        <Tooltip title={t('TX_INFORMATION.BUTTON.EDIT')} placement="top">
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
      )
    }
    const renderAddButton = () => isAddDisabled ? (
      <IconAddBoxDim
        key={data.id}
        className="p-0 ml-1 pe-none"
        title={isAdding ? t('TXP.MESSAGE.PRICE_MASTER_LOADING') : undefined}
        sx={{ backgroundColor: 'transparent !important' }}
      >
        <AddBoxIcon style={{ width: 30, height: 30 }} />
      </IconAddBoxDim>
    ) : (
      <IconAddBox
        key={data.id}
        onClick={() => { void handleAddOperative(data, planRemark) }}
        className="p-0 ml-1"
        sx={{ backgroundColor: 'transparent !important' }}
      >
        <AddBoxIcon style={{ width: 30, height: 30 }} />
      </IconAddBox>
    )
    const renderItemRemark = () => itemRemark ? (
      <Tooltip title={itemRemark} placement="top-start">
        <Typography
          component="div"
          sx={{
            color: colors.disabledGray,
            fontSize: 14,
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'normal',
          }}
        >
          {itemRemark}
        </Typography>
      </Tooltip>
    ) : null
    return (
      <>
        <TableRow key={`diagnosis-${no}`} className="main-table-row">
          <TableCell align="center" scope="row">
            {no + 1}
          </TableCell>
          {(isTdh && isWideTxpLayout && (
            <TableCell align="left" scope="row">
              <Tooltip title={data.teeth || ''}>
                <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {data.teeth || '-'}
                </Typography>
              </Tooltip>
            </TableCell>
          ))}
          {(isWideTxpLayout && (
            <>
              <TableCell align="left" scope="row">
                {data.operativeTypeName || '-'}
              </TableCell>
              <TableCell align="center" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {data.operativeCode || '-'}
                </Typography>
              </TableCell>
              <TableCell align="left" scope="row">
                {isTdh ? <>
                  <Typography component="div">{data.operativeName || '-'}</Typography>
                  {renderItemRemark()}
                </> : (data.operativeName || '-')}
              </TableCell>
              <TableCell align="right" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ whiteSpace: 'nowrap' }}>{renderPlanPrice(data)}</Box>
              </TableCell>
              {!isTdh && <>
                <TableCell align="left" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                  {data.dateUse ? dateToView(data.dateUse) : '-'}
                </TableCell>
                <TableCell align="left" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={doctorLabel}>
                    <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doctorLabel}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="left" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                  {data.duration || '-'} {
                    data.duration && (
                      data.durationUnit === 'YEARS' ? t('TXP.DATE.YEARS') : data.durationUnit === 'MONTH' ? t('TXP.DATE.MONTH') :
                        data.durationUnit === 'DAYS' ? t('TXP.DATE.DAYS') : data.durationUnit === 'HOURS' ? t('TXP.DATE.HOURS') : t('TXP.DATE.MINUTES') || '-'
                    )}
                </TableCell>
              </>}
              <TableCell align="center" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                {renderItemStatus(data)}
              </TableCell>
              {!props.history && (
                <TableCell align="center" scope="row" className="py-3 py-md-2">
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                    {renderEditButton()}
                    {renderAddButton()}
                  </Box>
                </TableCell>
              )}
            </>
          )) ||
            <>
              <TableCell align="left" scope="row">
                <Grid container spacing={1}>
                  {isTdh && <Grid item className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.TOOTH')}
                      </Typography>
                      <Typography> {data.teeth || '-'}</Typography>
                    </Box>
                  </Grid>}
                  <Grid item className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.TYPE')}
                      </Typography>
                      <Typography> {data.operativeTypeName || '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.CODE')}
                      </Typography>
                      <Typography> {data.operativeCode || '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.DETAIL')}
                      </Typography>
                      {isTdh ? <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography> {data.operativeName || '-'}</Typography>
                        {renderItemRemark()}
                      </Box> : <Typography> {data.operativeName || '-'}</Typography>}
                    </Box>
                  </Grid>
                  <Grid item xs={6} className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.PRICE')}
                      </Typography>
                      <Box> {renderPlanPrice(data)}</Box>
                    </Box>
                  </Grid>
                  {!isTdh && <>
                  <Grid item xs={6} className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {txpDateLabel}
                      </Typography>
                      <Typography>{data.dateUse ? dateToView(data.dateUse) : '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {txpDoctorLabel}
                      </Typography>
                      <Typography>{doctorLabel}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.TIME')}
                      </Typography>
                      <Typography>{data.duration} {data.durationUnit === 'YEARS' ? t('TXP.DATE.YEARS') : data.durationUnit === 'MONTH' ? t('TXP.DATE.MONTH') :
                        data.durationUnit === 'DAYS' ? t('TXP.DATE.DAYS') : data.durationUnit === 'HOURS' ? t('TXP.DATE.HOURS') : t('TXP.DATE.MINUTES') || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  </>}
                  <Grid item className='w-100'>
                    <Box className="d-flex">
                      <Typography sx={{ width: width >= 480 ? '130px' : '90px', fontWeight: 600, flex: 'none' }}>
                        {t('TXP.TABLE.STATUS')}
                      </Typography>
                      <Typography className="d-inline">
                        {renderItemStatus(data)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </TableCell>
              {!props.history && <TableCell align="center" scope="row" className="py-3 py-md-2">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  {renderEditButton()}
                  {renderAddButton()}
                </Box>
              </TableCell>
              }
            </>
          }
        </TableRow>
      </>
    )
  }

  return (
    <div className="w-100 h-100">
      <div className="d-flex justify-content-between align-items-center pb-2">
        <Row className="mx-0 w-100 align-items-center">
          <Col className="col-12 col-sm-4 px-1 justify-content-start">
            <Typography sx={{ fontSize: 18, fontWeight: 500, flex: 'none' }}>{t('TXP.TITLE')} ({txp?.length || 0})</Typography>
          </Col>
          {!props.history && <Col className="col-12 col-sm-8 px-1 ml-auto">
            <Row className='justify-content-md-end'>
              <Col className="col-12 col-sm-5 col-md-auto mt-2 mt-sm-0 px-2">
                <ButtonCustom disabled={!canCreate} variant="outlined" className={'px-2 px-md-3'} btnStyle={{ height: 42 }} textButton={t('TXP.BUTTON.ADD_TEMPLATE')} style={{ fontSize: 16, width: '100%' }} onClick={popupCreateTemplate} />
              </Col>
              <Col className="col-12 col-sm-7 col-md-auto mt-2 mt-sm-0 px-2">
                <ButtonCustom disabled={!canCreate} variant="outlined" btnStyle={{ height: 42 }} textButton={t('TXP.BUTTON.ADD_BY_USER')} style={{ fontSize: 16, width: '100%' }} onClick={popupCreateByUsers} />
              </Col>
            </Row>
          </Col>}
        </Row>
      </div>
      <Loading show={loading} type='softLoading' className='m-auto' />
      <Item className='custom-scroll' style={{ minHeight: 500 }}>
        {_.map(orderedTxp, (val: any, index: number) => {
          return (
            <TableWidth className={`pt-2 ${tdhTxpTableLayout ? 'txp-table' : ''}`}>
              <TxInformationBox
                headCells={props.history ? headCellsHistory : headCells}
                renderData={renderData}
                titleName={val.treatmentPlanName}
                arrayTable={val.patientTreatmentPlanItems}
                status={val.status}
                remark={val.remark}
                createdAt={dateTimeToView(val.createdAt)}
                createdBy={val.createdBy}
                txp={true}
                onEdit={() => onEdit(val)}
                onEditItem={isTdh ? () => onEdit(val) : undefined}
                onDelete={() => updateStatus(val, 'DELETED')}
                onClosePlan={() => updateStatus(val, 'CLOSE')}
                onCancelPlan={() => updateStatus(val, 'CANCEL')}
                onPendingPlan={() => updateStatus(val, 'PENDING')}
                onShowTreatmentPlanForm={() => {
                  setSelectedPlan(val)
                  setShowPlanForm(true)
                }}
                onAfterPrint={() => onTreatmentPlanPrinted(val, 'TREATMENT_PLAN')}
                doctors={doctors}
                patient={props.patient}
                history={props.history}
                hasEditTreatmentPlan={canEdit ? 'PERMISS' : 'UNPERMISS'}
                hasDeleteTreatmentPlan={canDelete ? 'PERMISS' : 'UNPERMISS'}
                type={props.type}
                  hasSavedSignature={!!val.doctorSignature}
                  tableMinWidth={tdhTxpTableLayout?.minWidth}
                  stickyLastColumn={isTdh && !props.history}
                />
            </TableWidth>
          )
        })}
      </Item>

      {createByUsers && (
        <PopupCreateByUsers
          header={header}
          isShow={createByUsers}
          onSubmit={(val: 'true' | '') => {
            if (val === 'true') loadData()
          }}
          txp={true}
          hasCreateTreatmentPlan={canCreate ? 'PERMISS' : 'UNPERMISS'}
          doctors={doctors}
          operativeTypes={operativeTypes}
          showTemplate={createTemplate}
          patientCaseId={props.patient?.patientCase?.patientCaseId}
          patientId={patientId}
          edit={edit}
          setIsShow={(show: boolean) => {
            setCreateByUsers(show)
            setEdit('')
          }}
          operativeType={operativeType}
          opertatives={opertatives}
        />
      )}

      {showPlanForm && (
        <PopupTreatmentPlanForm
          isShow={showPlanForm}
          setIsShow={setShowPlanForm}
          plan={selectedPlan}
          patient={props.patient}
          doctors={doctors}
          onSaved={loadData}
          onAfterPrint={(signatureState) => onTreatmentPlanPrinted(selectedPlan, 'TREATMENT_PLAN_FORM', signatureState)}
        />
      )}
    </div>
  )
}
